import { useTranslation } from 'react-i18next';
import { toCurrencyString } from '../../lib/currency-helpers.ts';
import { formatShortDate } from '../../lib/date-helpers.ts';
import { useResolvedLocale } from '../../lib/hooks/locale.ts';
import { AppliedFilterList, type AppliedFilter } from './applied-filter-list.tsx';
import type { VerticalFilterOption } from './types/filter-types.ts';
import { DateRangeComparison, RelativeTimeUnit } from './types/filter-types.ts';

export type AnyFilter = Record<string, unknown>;

type VerticalAppliedFiltersProps<TFilter extends AnyFilter> = {
    filters: (VerticalFilterOption | undefined)[];
    defaultFilters: TFilter;
    appliedFilters: TFilter;
    onApplyFilters: (filters: TFilter) => void;
    defaultCurrency: string;
} & Omit<React.ComponentProps<typeof AppliedFilterList>, 'filters' | 'onRemoveAllFilters'>;

export function VerticalAppliedFilters<TFilter extends AnyFilter>({
    filters,
    defaultFilters,
    appliedFilters,
    onApplyFilters,
    defaultCurrency,
    ...props
}: VerticalAppliedFiltersProps<TFilter>) {
    const { t } = useTranslation('filters');
    const locale = useResolvedLocale();

    function handleRemoveAllFilters(): void {
        onApplyFilters(defaultFilters);
    }

    const appliedFilterOptions = filters.flatMap((filter): AppliedFilter[] => {
        if (!filter) {
            return [];
        }

        const appliedValue = appliedFilters[filter.id as keyof TFilter];

        const handleRemove = () => {
            // APP-1755: nulls are treated as values, so to indicate the filter removal we need to set the value to undefined
            onApplyFilters({ ...appliedFilters, [filter.id]: undefined });
        };

        switch (filter.type) {
            case 'checkbox': {
                if (Array.isArray(appliedValue) && appliedValue.length > 0) {
                    return appliedValue.flatMap((value) => {
                        const option = filter.options.find((opt) => opt.value === value);

                        return [
                            {
                                key: `${filter.id}-${value}`,
                                category: filter.label,
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- legacy code
                                filter: option?.label ?? value,
                                onRemove: () => {
                                    onApplyFilters({
                                        ...appliedFilters,
                                        [filter.id]: appliedValue.filter((v) => v !== value),
                                    });
                                },
                            },
                        ];
                    });
                }
                break;
            }
            case 'radio': {
                if (typeof appliedValue === 'string' && appliedValue) {
                    const option = filter.options.find((opt) => opt.value === appliedValue);
                    if (option) {
                        return [
                            {
                                key: filter.id,
                                category: filter.label,
                                filter: option.label,
                                onRemove: handleRemove,
                            },
                        ];
                    }
                }
                break;
            }
            case 'dateRange': {
                if (appliedValue && typeof appliedValue === 'object') {
                    const dateRange = appliedValue as
                        | { from?: Date; to?: Date }
                        | { unit: RelativeTimeUnit; count: number; comparison?: DateRangeComparison };

                    let label = '';
                    if ('unit' in dateRange && 'count' in dateRange) {
                        // Handle relative date ranges
                        let unitLabel = '';
                        switch (dateRange.unit) {
                            case RelativeTimeUnit.DAYS:
                                unitLabel = t('date_range_filter__unit_days');
                                break;
                            case RelativeTimeUnit.WEEKS:
                                unitLabel = t('date_range_filter__unit_weeks');
                                break;
                            case RelativeTimeUnit.MONTHS:
                                unitLabel = t('date_range_filter__unit_months');
                                break;
                        }

                        // Determine category based on comparison (newer vs older)
                        const comparison = dateRange.comparison || DateRangeComparison.NEWER;
                        const categoryLabel =
                            comparison === DateRangeComparison.OLDER
                                ? t('date_range_filter__older_than')
                                : t('date_range_filter__newer_than');

                        return [
                            {
                                key: filter.id,
                                category: `${filter.label} ${categoryLabel}`,
                                filter: `${dateRange.count} ${unitLabel}`,
                                onRemove: handleRemove,
                            },
                        ];
                    } else if ('from' in dateRange && 'to' in dateRange) {
                        // Handle custom date ranges
                        const from = dateRange.from ? formatShortDate(dateRange.from, locale) : '';
                        const to = dateRange.to ? formatShortDate(dateRange.to, locale) : '';
                        if (from && to) {
                            label = `${from} - ${to}`;
                        }

                        return [
                            {
                                key: filter.id,
                                category: `${filter.label} ${t('date_range_filter__range_mode')}`,
                                filter: label,
                                onRemove: handleRemove,
                            },
                        ];
                    }
                }
                break;
            }
            case 'priceRange': {
                if (appliedValue && typeof appliedValue === 'object') {
                    const range = appliedValue as { min?: number; max?: number };

                    if (range.min !== undefined || range.max !== undefined) {
                        let label = '';

                        if (range.min !== undefined) {
                            label = toCurrencyString(range.min, locale, 2, defaultCurrency);
                        } else if (range.max !== undefined) {
                            label = toCurrencyString(range.max, locale, 2, defaultCurrency);
                        }

                        if (label) {
                            return [
                                {
                                    key: filter.id,
                                    category: filter.label,
                                    filter: label,
                                    onRemove: handleRemove,
                                },
                            ];
                        }
                    }
                }
                break;
            }
            case 'numberRange': {
                if (appliedValue && typeof appliedValue === 'object') {
                    const range = appliedValue as { min?: number; max?: number };

                    if (range.min !== undefined || range.max !== undefined) {
                        let label = '';
                        const unit = filter.unit || '';

                        if (range.min !== undefined && range.max !== undefined) {
                            label = `${unit}${range.min} - ${unit}${range.max}`;
                        } else if (range.min !== undefined) {
                            label = `${unit}${range.min}`;
                        } else if (range.max !== undefined) {
                            label = `${unit}${range.max}`;
                        }

                        if (label) {
                            return [
                                {
                                    key: filter.id,
                                    category: filter.label,
                                    filter: label,
                                    onRemove: handleRemove,
                                },
                            ];
                        }
                    }
                }
                break;
            }
        }

        return [];
    });

    if (appliedFilterOptions.length === 0) {
        return null;
    }

    return (
        <AppliedFilterList
            onRemoveAllFilters={handleRemoveAllFilters}
            filters={appliedFilterOptions}
            {...props}
        />
    );
}
